import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { formatDateTime } from '../utils/formatDate';
import { useIssueComments } from '../hooks/useIssueComments';
import { useAddComment } from '../hooks/useAddComment';
import { useDeleteComment } from '../hooks/useDeleteComment';
import MentionPicker, { mentionOptionId, MENTION_LISTBOX_ID } from './MentionPicker';
import { Badge } from './ui/badge';
import { deriveLiveMentions, insertMention, removeMentionsByName } from '../utils/mentionText';

// Matches an in-progress "@word" token up to the caret, e.g. "hi @jo" -> "jo".
// \p{L}/\p{N} (not \w) so accented/hyphenated names (e.g. "@Fréd", "@Jean-Paul")
// keep matching instead of closing the dropdown mid-typing.
// Anchored on the caret (not the whole text) so a trailing "@word" elsewhere
// in the field never opens the dropdown while the caret is somewhere else.
const MENTION_TRIGGER_REGEX = /(?:^|\s)@([\p{L}\p{N}._-]*)$/u;

const IssueComments = ({ issueKey, currentUser, onImageClick }) => {
  const { t } = useTranslation();
  const [newCommentText, setNewCommentText] = useState('');
  const [expandedComments, setExpandedComments] = useState({});
  // Colleagues picked from the dropdown during this composition. The TEXT is the
  // source of truth: a mention only counts while its "@Display Name" tag is in it,
  // so `mentions` is derived, never stored (badge and payload can't drift apart).
  const [knownUsers, setKnownUsers] = useState([]);
  // null = dropdown closed; { query, anchor: {start, end} } when open.
  // query/anchor always change together, so a single state avoids the two
  // getting out of sync (e.g. one cleared without the other on submit).
  const [mentionToken, setMentionToken] = useState(null);
  const [mentionResults, setMentionResults] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const textareaRef = useRef(null);
  // Caret position to restore after a mention selection commits (mouse
  // selection loses focus/caret to the clicked option button; keyboard
  // selection never leaves the textarea, so this is a no-op for it).
  const pendingCaretRef = useRef(null);
  const { comments, isLoading, error: fetchError } = useIssueComments(issueKey);
  const mentions = useMemo(() => deriveLiveMentions(newCommentText, knownUsers), [newCommentText, knownUsers]);

  const addComment = useAddComment(issueKey, {
    onSuccess: () => {
      setNewCommentText('');
      setKnownUsers([]);
      setMentionToken(null);
    },
  });

  const deleteComment = useDeleteComment(issueKey);
  const errorActions = fetchError?.message || addComment.error?.message || deleteComment.error?.message;

  const handleCommentTextChange = (e) => {
    const value = e.target.value;
    setNewCommentText(value);

    const match = value.slice(0, e.target.selectionStart).match(MENTION_TRIGGER_REGEX);
    if (match) {
      const word = match[1];
      setMentionToken({
        query: word,
        anchor: { start: e.target.selectionStart - word.length - 1, end: e.target.selectionStart },
      });
      setActiveIndex(0);
    } else {
      setMentionToken(null);
    }
  };

  const handleMentionSelect = (user) => {
    // Registered by accountId; inserted unconditionally, so mentioning the same
    // person twice writes the name twice (both become mention nodes, one notification).
    setKnownUsers((prev) => (prev.some((m) => m.accountId === user.accountId) ? prev : [...prev, user]));
    if (mentionToken) {
      const { text, caret } = insertMention(newCommentText, mentionToken.anchor, user.displayName);
      pendingCaretRef.current = caret;
      setNewCommentText(text);
    }
    setMentionToken(null);
  };

  // Runs after the stripped text commits to the DOM; restores focus/caret to
  // the textarea (a mouse selection leaves focus on the clicked option button).
  useEffect(() => {
    if (pendingCaretRef.current === null) return;
    const pos = pendingCaretRef.current;
    pendingCaretRef.current = null;
    textareaRef.current?.focus();
    textareaRef.current?.setSelectionRange(pos, pos);
  }, [newCommentText]);

  // The × is an explicit "no": drop the tag from the text AND forget the person,
  // so retyping the name does not silently re-attach the mention. A keyboard
  // deletion keeps them in `knownUsers`, so that one does re-attach.
  const handleMentionRemove = (user) => {
    setNewCommentText((prev) => removeMentionsByName(prev, user.displayName, knownUsers));
    setKnownUsers((prev) => prev.filter((m) => m.displayName !== user.displayName));
  };

  // Stable identity: MentionPicker's effect only reports new results when its
  // own query actually changes fetched data, not on every parent re-render
  // (e.g. an arrow-key press) — an unstable callback would re-fire the effect
  // on every keystroke/arrow-press and stomp the just-moved activeIndex back to 0.
  const handleMentionResultsChange = useCallback((results) => {
    setMentionResults(results);
    setActiveIndex(0);
  }, []);

  // Stable identity for the same reason as handleMentionResultsChange above:
  // an inline arrow here would re-attach MentionPicker's Escape window
  // listener on every keystroke instead of once per dropdown open/close.
  const handleMentionQueryChange = useCallback((q) => {
    if (q === null) setMentionToken(null);
  }, []);

  const handleTextareaKeyDown = (e) => {
    if (!mentionToken) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (mentionResults.length ? (i + 1) % mentionResults.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (mentionResults.length ? (i - 1 + mentionResults.length) % mentionResults.length : 0));
    } else if (e.key === 'Enter' && mentionResults[activeIndex]) {
      e.preventDefault();
      handleMentionSelect(mentionResults[activeIndex]);
    }
  };

  const handleAddComment = (e) => {
    e.preventDefault();
    if (!newCommentText.trim()) {
      return;
    }
    const userDisplayName = currentUser?.name || 'Utilisateur';
    const commentTextWithAuthor = `[${userDisplayName}] ${newCommentText.trim()}`;
    addComment.mutate({ text: commentTextWithAuthor, mentions });
  };

  const handleDeleteComment = (commentId) => {
    if (!window.confirm(t('Are you sure you want to delete this comment?'))) {
      return;
    }
    deleteComment.mutate(commentId);
  };

  const toggleComment = (commentIndex) => {
    setExpandedComments(prev => ({
      ...prev,
      [commentIndex]: !prev[commentIndex]
    }));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b border-gray-100 pb-2">
        {t('Comments')} ({comments.length})
      </h3>

      {errorActions && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {errorActions}
        </div>
      )}

      {/* Add comment form */}
      <form onSubmit={handleAddComment} className="mb-6">
        <div className="mb-3">
          <textarea
            ref={textareaRef}
            value={newCommentText}
            onChange={handleCommentTextChange}
            onKeyDown={handleTextareaKeyDown}
            placeholder={t('Add a comment…')}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary resize-none"
            disabled={addComment.isPending}
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={mentionToken !== null}
            aria-controls={MENTION_LISTBOX_ID}
            aria-activedescendant={
              mentionToken !== null && mentionResults[activeIndex] ? mentionOptionId(activeIndex) : undefined
            }
          />
        </div>

        {mentionToken !== null && (
          <div className="mb-3">
            <MentionPicker
              issueKey={issueKey}
              query={mentionToken.query}
              onQueryChange={handleMentionQueryChange}
              activeIndex={activeIndex}
              onResultsChange={handleMentionResultsChange}
              onSelect={handleMentionSelect}
            />
          </div>
        )}

        {mentions.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-1.5">
            {mentions.map((user) => (
              <Badge key={user.accountId} variant="secondary" className="flex items-center gap-1 pr-1">
                {user.displayName}
                <button
                  type="button"
                  onClick={() => handleMentionRemove(user)}
                  aria-label={t('Remove {{name}}', { name: user.displayName })}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-black/10"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        <button
          type="submit"
          disabled={addComment.isPending || !newCommentText.trim()}
          className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {addComment.isPending ? t('Adding…') : t('Add a comment')}
        </button>
      </form>

      {/* Comments list */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : comments.length > 0 ? (
        <div className="space-y-2">
          {comments.map((comment, index) => {
            const isExpanded = expandedComments[index];
            const isDeleting = deleteComment.isPending && deleteComment.variables === comment.id;

            return (
              <div key={comment.id || index} className="border border-gray-100 rounded-lg">
                <button
                  onClick={() => toggleComment(index)}
                  className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 rounded-lg transition-colors"
                  disabled={isDeleting}
                >
                  <div className="flex items-center flex-1">
                    <span className="text-sm font-medium text-gray-900">
                      {comment.author || t('User')}
                    </span>
                    <span className="ml-2 text-xs text-gray-500">
                      {comment.created ? formatDateTime(comment.created) : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {isDeleting && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    )}
                    <svg
                      className={`w-5 h-5 text-gray-500 transform transition-transform ${
                        isExpanded ? 'rotate-90' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-3 pb-3 border-t border-gray-100 mt-2 pt-3">
                    {comment.text && (
                      <div className="text-sm text-gray-700 mb-3 whitespace-pre-wrap">
                        {comment.text}
                      </div>
                    )}

                    {comment.images && comment.images.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {comment.images.map((image, imgIndex) => (
                          <div
                            key={imgIndex}
                            className="border border-gray-300 rounded cursor-pointer"
                            onClick={() => onImageClick && onImageClick({
                              url: image.url || image.src || image,
                              filename: image.filename || `Comment image ${imgIndex + 1}`
                            })}
                          >
                            <img
                              src={image.url || image.src || image}
                              alt={image.filename || `Comment image ${imgIndex + 1}`}
                              className="w-20 h-20 object-cover rounded"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Delete button - only show if user has permission (admin/manager or comment author) */}
                    {(currentUser?.role === 'admin' || currentUser?.role === 'manager' ||
                      (currentUser?.username && comment.author &&
                       currentUser.username.toLowerCase() === comment.author.toLowerCase())) && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteComment(comment.id);
                          }}
                          disabled={isDeleting}
                          className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isDeleting ? t('Deleting…') : t('Delete')}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-gray-500 text-center py-8">{t('No comment')}</p>
      )}
    </div>
  );
}

export default IssueComments;
